import React, { useEffect, useState, useRef } from 'react';
import { QuestionRendererProps } from './types';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle } from 'lucide-react';
import { GridShadeConfig } from '../../../content/types';
export function GridShadeRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = (question.data?.gridShade || question.data || {}) as GridShadeConfig;
  const gridRows = config.gridRows || 1;
  const gridCols = config.gridCols || 1;
  const targetCount = config.targetCount || 0;
  const mode = config.mode || 'shade_count';
  const preShaded = config.preShaded || [];
  const showCounter = config.showCounter ?? true;
  const totalCells = gridRows * gridCols;
  const [selectedCells, setSelectedCells] = useState<number[]>([]);
  const [hoveredCell, setHoveredCell] = useState<number | null>(null);
  const prevValidateRef = useRef(false);
  useEffect(() => {
    setSelectedCells(preShaded);
    prevValidateRef.current = false;
  }, [question.id, preShaded]);
  useEffect(() => {
    if (!shouldValidate) prevValidateRef.current = false;
  }, [shouldValidate]);
  useEffect(() => {
    // Ready when at least one cell is selected (or if target is 0, which is rare)
    onReadyChange?.(selectedCells.length > 0 || targetCount === 0);
  }, [selectedCells.length, targetCount, onReadyChange]);
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current) {
      prevValidateRef.current = true;
      onAnswer(JSON.stringify(selectedCells));
    }
  }, [shouldValidate, selectedCells, onAnswer]);
  const toggleCell = (index: number) => {
    if (shouldValidate || preShaded.includes(index)) return;
    setSelectedCells(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };
  const isCorrect = selectedCells.length === targetCount;
  const isTooMany = selectedCells.length > targetCount;
  // Determine if we should show the exact count or just a fraction indicator
  const showExactCount = mode !== 'shade_fraction' || showCounter;
  const showFractionIndicator = mode === 'shade_fraction' && !showCounter;
  return <div className="w-full max-w-2xl mx-auto flex flex-col items-center gap-6" data-id="element-7171">
      {(showCounter || showFractionIndicator) && <div className="flex items-center gap-3 bg-slate-50 px-6 py-3 rounded-2xl border-2 border-slate-200" data-id="element-7172">
          <span className="text-lg font-bold text-slate-600" data-id="element-7173">
            {showFractionIndicator ? 'Gedeelte:' : 'Gekleurd:'}
          </span>

          {showExactCount ? <span className={`text-2xl font-black ${shouldValidate ? isCorrect ? 'text-emerald-500' : 'text-red-500' : isTooMany ? 'text-amber-500' : 'text-indigo-600'}`} data-id="element-7174">
              {selectedCells.length}
            </span> : <span className="text-2xl font-black text-indigo-400" data-id="element-7175">?</span>}

          {(mode === 'shade_fraction' || showFractionIndicator) && <>
              <span className="text-xl font-bold text-slate-400" data-id="element-7176">/</span>
              <span className="text-2xl font-black text-slate-600" data-id="element-7177">
                {totalCells}
              </span>
            </>}
          {shouldValidate && <div className="ml-2" data-id="element-7178">
              {isCorrect ? <CheckCircle2 className="w-6 h-6 text-emerald-500" data-id="element-7179" /> : <XCircle className="w-6 h-6 text-red-500" data-id="element-7180" />}
            </div>}
        </div>}

      <div className="grid gap-1 sm:gap-2 p-3 sm:p-4 bg-white rounded-2xl border-2 border-slate-200 shadow-sm" style={{
      gridTemplateColumns: `repeat(${gridCols}, minmax(32px, 1fr))`,
      gridTemplateRows: `repeat(${gridRows}, minmax(32px, 1fr))`,
      aspectRatio: `${gridCols} / ${gridRows}`,
      maxWidth: '400px',
      width: '100%'
    }} onMouseLeave={() => setHoveredCell(null)} data-id="element-7181">
        {Array.from({
        length: totalCells
      }).map((_, index) => {
        const isSelected = selectedCells.includes(index);
        const isPreShaded = preShaded.includes(index);
        // Calculate row and col for highlight logic
        const row = Math.floor(index / gridCols);
        const col = index % gridCols;
        const isHoveredRowOrCol = hoveredCell !== null && !shouldValidate && (Math.floor(hoveredCell / gridCols) === row || hoveredCell % gridCols === col);
        let cellClass = 'bg-slate-100 border-slate-200';
        if (isSelected) {
          if (shouldValidate) {
            cellClass = isCorrect ? 'bg-emerald-500 border-emerald-600' : 'bg-red-500 border-red-600';
          } else {
            cellClass = 'bg-indigo-500 border-indigo-600 hover:bg-indigo-400';
          }
        } else if (!isPreShaded && !shouldValidate) {
          if (hoveredCell === index) {
            cellClass = 'bg-slate-300 border-slate-400';
          } else if (isHoveredRowOrCol) {
            cellClass = 'bg-slate-200 border-slate-300';
          } else {
            cellClass = 'bg-slate-100 border-slate-200 hover:bg-slate-200';
          }
        }
        if (isPreShaded) {
          cellClass = 'bg-slate-800 border-slate-900 opacity-50 cursor-not-allowed';
        }
        return <motion.button key={index} whileTap={shouldValidate || isPreShaded ? {} : {
          scale: 0.85,
          backgroundColor: isSelected ? '#818cf8' : '#cbd5e1' // indigo-400 or slate-300
        }} onMouseEnter={() => setHoveredCell(index)} onClick={() => toggleCell(index)} disabled={shouldValidate || isPreShaded} className={`w-full h-full rounded-md sm:rounded-lg border-2 transition-colors duration-150 ${cellClass} ${shouldValidate || isPreShaded ? 'cursor-default' : 'cursor-pointer'}`} aria-label={`Vakje ${index + 1} ${isSelected ? 'gekleurd' : 'leeg'}`} aria-pressed={isSelected} data-id="element-7182" />;
      })}
      </div>
    </div>;
}